import { defineStore } from 'pinia';
import { ref } from 'vue';

import { getRequestHandler, requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { OnboardingAdminRequests as requests } from '@/services/requests/onboarding/OnboardingRequests';
import { UsersRequests } from '@/services/requests/users/UsersRequests';
import { IApiResponse } from '@/types/interfaces';
import { CorporateDocumentType, DocumentSide } from '@/types/interfaces/Document.interface';
import { formatOnboardingPersonalDocuments } from '@/utils/formatOnboardingUserData';

export const useOnboardingAdminSoleTraderStore = defineStore('OnboardingAdminSoleTrader', () => {
  const isApplicationLoaded = ref(false);
  const id = ref('');
  const email = ref('');
  const phone = ref('');
  const completedStages = ref<Onboarding.SoleTrader.Stages[]>([]);
  const applicationStatus = ref<Onboarding.ApplicationStatus>('ONBOARDING');
  const applicationType = ref<Onboarding.ApplicationType>('SOLE_TRADER');
  const personalDetails = ref<Onboarding.SoleTrader.Details>({
    firstName: '',
    lastName: '',
    nationalityIso3: '',
    dateOfBirth: '',
    mailingAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryIso3: '',
      postalCode: '',
    },
  });
  const businessInfo = ref<Onboarding.SoleTrader.BusinessInfo>({
    businessActivityDescription: '',
    businessIndustry: undefined,
    businessLegalName: '',
    countryIso3: '',
    dateOfBusinessStart: '',
    email: '',
    vatNumber: '',
    website: '',
    annualTurnover: undefined,
  });
  const userDetails = ref<Onboarding.SoleTrader.FullApplication['userDetails']>({
    userId: '',
    organizationId: '',
    profileOrganizationId: '',
    permissions: [],
    email: '',
    phone: '',
  });
  const riskLevel = ref<Onboarding.RiskLevel | undefined>();
  const createAt = ref('');
  const updatedAt = ref('');
  const blocked = ref(false);
  const banned = ref<boolean>();
  const contract = ref();
  const documentsData = ref();
  const reviewedDocuments = ref();

  function setData(data: Onboarding.SoleTrader.FullApplication) {
    id.value = data.id;
    email.value = data.email || '';
    phone.value = data.phone || '';
    completedStages.value = data.completedStages || [];
    applicationStatus.value = data.applicationStatus || 'ONBOARDING';
    applicationType.value = data.applicationType || 'SOLE_TRADER';

    if (data.personalDetails) {
      personalDetails.value = data.personalDetails;
    }

    if (data.businessInfo) {
      businessInfo.value = data.businessInfo;
    }

    if (data.userDetails) {
      userDetails.value = data.userDetails;
    }

    riskLevel.value = data.riskLevel;
    createAt.value = data.createAt || '';
    updatedAt.value = data.updatedAt || '';
    blocked.value = data.blocked || false;
    banned.value = data.banned;
    contract.value = data.contract;
    documentsData.value = data.documents ? formatOnboardingPersonalDocuments(data.documents) : undefined;
    reviewedDocuments.value = data.reviewedDocuments;
  }

  const fetchBannedStatus = getRequestHandler(UsersRequests.getUser, {
    onSuccess: (res) => {
      banned.value = res.banned;
    },
  });

  interface SoleTraderDocument {
    type: CorporateDocumentType;
    formData: FormData;
    issuingCountryIso3?: string;
    side?: DocumentSide;
  }

  async function fetchApplication(
    applicationId: string,
    force?: boolean,
  ): Promise<IApiResponse<Onboarding.SoleTrader.FullApplication> | null> {
    if (!force && isApplicationLoaded.value) return null;
    return requestHandler(() => requests.SoleTrader.getApplication(applicationId), {
      pending: (newValue) => {
        isApplicationLoaded.value = !newValue;
      },
      onSuccess: (res) => {
        if (res.userDetails?.userId) fetchBannedStatus(res.userDetails.userId);
        setData(res);
      },
    });
  }

  const updatePersonalDetails = getRequestHandler(
    (data: Onboarding.SoleTrader.Details) => requests.SoleTrader.updatePersonalDetails(id.value, data),
    { onSuccess: setData },
  );

  const updateBusinessInfo = getRequestHandler(
    (data: Onboarding.SoleTrader.BusinessInfo) => requests.SoleTrader.updateBusinessInfo(id.value, data),
    { onSuccess: setData },
  );

  const verifyApplication = getRequestHandler(
    () => requests.SoleTrader.verifyApplication(id.value),
    { onSuccess: setData },
  );

  const updateStatus = getRequestHandler(
    (status: Exclude<Onboarding.UpdateApplicationStatus, 'verified'>) => {
      switch (status) {
        case 'decline':
          return requests.SoleTrader.declineApplication(id.value);
        case 'no-response':
          return requests.SoleTrader.noResponseApplication(id.value);
        case 'onboarding':
          return requests.SoleTrader.onOnboardingApplication(id.value);
        case 'non-verified':
          return requests.SoleTrader.onNonVerifiedApplication(id.value);
        default:
          return requests.SoleTrader.updateStatus(id.value, status);
      }
    },
    { onSuccess: setData },
  );

  const documentsHandler = {
    get: getRequestHandler(
      (fileData: {
        type: CorporateDocumentType;
        side: DocumentSide;
      }) => requests.SoleTrader.document.get(id.value, fileData),
    ),
    upload: getRequestHandler(
      (fileData: SoleTraderDocument) => requests.SoleTrader.document.upload(id.value, fileData),
      { onSuccess: setData },
    ),
  };

  function updateContract(_: string, contractId: string) {
    return requestHandler(
      () => requests.SoleTrader.updateContract(id.value, contractId),
      {
        errorNotification: false,
        onSuccess: setData,
      },
    );
  }

  const uploadDocument = getRequestHandler(
    (fileData: SoleTraderDocument) => requests.SoleTrader.document.upload(id.value, fileData),
    { onSuccess: setData },
  );

  const updateRiskLevelAction = getRequestHandler(
    (riskLevelValue: Onboarding.RiskLevel) => requests.SoleTrader.updateRiskLevel(id.value, riskLevelValue),
    { onSuccess: setData },
  );

  async function toggleBlockStatus() {
    const request = blocked.value
      ? requests.Shared.unblockApplication
      : requests.Shared.blockApplication;

    return requestHandler(
      () => request(id.value),
      {
        onSuccess: (res) => {
          blocked.value = res.blocked;
        },
      },
    );
  }

  async function unban() {
    const userIdValue = userDetails.value?.userId;
    if (userIdValue === undefined) throw new Error('User ID is undefined');
    return requestHandler(() => UsersRequests.unban(userIdValue), {
      successNotification: true,
      onSuccess: () => {
        banned.value = false;
      },
    });
  }

  const updateUserContacts = getRequestHandler(
    (newContact: Record<string, string | undefined>) => requests.Shared.updateUserContact(id.value, newContact),
  );

  const close = getRequestHandler(
    () => requests.Shared.closeAccount(id.value),
    {
      onSuccess: (res) => {
        applicationStatus.value = res.applicationStatus;
      },
      errorNotification: false,
    },
  );

  function $reset() {
    isApplicationLoaded.value = false;
    id.value = '';
    email.value = '';
    phone.value = '';
    completedStages.value = [];
    applicationStatus.value = 'ONBOARDING';
    applicationType.value = 'SOLE_TRADER';
    personalDetails.value = {
      firstName: '',
      lastName: '',
      nationalityIso3: '',
      dateOfBirth: '',
      mailingAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        countryIso3: '',
        postalCode: '',
      },
    };
    businessInfo.value = {
      businessActivityDescription: '',
      businessIndustry: undefined,
      businessLegalName: '',
      countryIso3: '',
      dateOfBusinessStart: '',
      email: '',
      vatNumber: '',
      website: '',
      annualTurnover: undefined,
    };
    userDetails.value = {
      userId: '',
      organizationId: '',
      profileOrganizationId: '',
      permissions: [],
      email: '',
      phone: '',
    };
    riskLevel.value = undefined;
    createAt.value = '';
    updatedAt.value = '';
    blocked.value = false;
    banned.value = undefined;
    contract.value = undefined;
    documentsData.value = undefined;
    reviewedDocuments.value = undefined;
  }

  return {
    isApplicationLoaded,
    id,
    email,
    phone,
    completedStages,
    applicationStatus,
    applicationType,
    personalDetails,
    businessInfo,
    userDetails,
    riskLevel,
    createAt,
    updatedAt,
    blocked,
    banned,
    contract,
    documents: documentsData,
    reviewedDocuments,

    documentsHandler,
    fetchApplication,
    updatePersonalDetails,
    updateBusinessInfo,
    verifyApplication,
    updateStatus,
    updateContract,
    uploadDocument,
    updateRiskLevel: updateRiskLevelAction,
    toggleBlockStatus,
    unban,
    updateUserContacts,
    close,

    $reset,
  };
});
